import type { Level, TheoremReference, World } from '../types/world';
import { matchesLevelKey } from './ids';

export function areAllTacticsAllowed(level: Level, lecturerOverride = false): boolean {
  return lecturerOverride || level.allowAllTactics === true || level.allowedTactics === 'all';
}

export function getLearningResources(world: World, level: Level, completed: string[], globalTactics: string[]) {
  const index = world.levels.findIndex(item => item.id === level.id);
  const unlocked = (reference: TheoremReference) => !reference.unlockedBy ||
    (world.levels.slice(0, index).some(item => item.id === reference.unlockedBy) &&
      completed.some(key => matchesLevelKey(key, world.id, reference.unlockedBy!)));
  return {
    tactics: Array.isArray(level.allowedTactics) ? level.allowedTactics : globalTactics,
    theorems: level.availableTheorems?.filter(unlocked) ?? world.availableTheorems ?? [],
    lockedTheorems: level.availableTheorems?.filter(reference => !unlocked(reference)).map(reference => reference.name) ?? [],
  };
}

// Preserve sentence boundaries while removing nested Rocq comments and strings.
export function maskRocqComments(code: string): string {
  let depth = 0;
  let string = false;
  let output = '';
  for (let i = 0; i < code.length; i++) {
    const pair = code.slice(i, i + 2);
    if (!string && pair === '(*') { depth++; output += '  '; i++; }
    else if (depth && pair === '*)') { depth--; output += '  '; i++; }
    else if (depth) output += code[i] === '\n' ? '\n' : ' ';
    else if (code[i] === '"') {
      if (string && code[i + 1] === '"') { output += '  '; i++; }
      else { string = !string; output += ' '; }
    } else output += string ? ' ' : code[i];
  }
  return output;
}

/** A course syntax check, followed by the normal Rocq proof check. */
export function checkLearningPolicy(
  code: string,
  level: Level,
  lockedTheorems: string[] = [],
  lecturerOverrides: { allowAllTactics?: boolean; allowAllTheorems?: boolean } = {},
): string[] {
  if (level.allowedTactics === undefined && level.allowAllTactics !== true) return [];
  const issues = new Set<string>();
  const allowed = !areAllTacticsAllowed(level, lecturerOverrides.allowAllTactics) && Array.isArray(level.allowedTactics)
    ? new Set(level.allowedTactics) : null;
  const forbidden = new Set(lecturerOverrides.allowAllTheorems ? [] : [...(level.forbiddenTheorems ?? []), ...lockedTheorems]);
  const clean = maskRocqComments(code);
  if (allowed && /\bltac\s*:/.test(clean)) issues.add('Use the proof tactics taught in this level.');
  let proof = false;
  for (const sentence of clean.split(/\.(?=\s|$)/)) {
    const text = sentence.trim();
    const startsProof = /^Proof(?:\s|$)/.test(text);
    if (startsProof) {
      if (allowed && text !== 'Proof') issues.add('Start the tactic proof with Proof.');
      proof = true;
    }
    if (/^(Qed|Defined|Abort|Admitted)$/.test(text)) {
      if (text === 'Admitted' || text === 'Abort') issues.add(`Incomplete proof: ${text}`);
      proof = false; continue;
    }
    if (/^(Axiom|Parameter|Parameters|Axioms)\b/.test(text) ||
        (allowed && /^(Ltac|Tactic\s+Notation)\b/.test(text))) {
      issues.add('Use the definitions and proof tactics taught in this level.');
    }
    if (/^(Check|Print|About|Search|SearchPattern|Locate|Set|Unset)\b/.test(text)) continue;
    if (!proof && /^(Require|Import|From)\b/.test(text)) continue;
    const references = !proof ? text.replace(/^(?:Theorem|Lemma|Example|Definition|Fixpoint)\s+[\w']+/, '') : text;
    for (const name of references.match(/[A-Za-z_][\w']*(?:\.[A-Za-z_][\w']*)*/g) ?? []) {
      if ([...forbidden].some(blocked => name === blocked || name.endsWith('.' + blocked) || name === blocked.split('.').pop())) issues.add(`Theorem not yet available: ${name}`);
    }
    if (startsProof || !proof || !allowed) continue;
    // Semicolons combine tactics; brackets after a semicolon select branches.
    // Arguments such as induction patterns [| n IH] remain part of the tactic.
    for (const sequence of text.split(/;|\|\|/)) {
      const trimmed = sequence.trim().replace(/^[{}\s\-+*]+/, '').replace(/^\d+\s*:\s*/, '');
      const branches = trimmed.startsWith('[') ? trimmed.slice(1).split('|') : [trimmed];
      for (const branch of branches) {
        const name = branch.replace(/^[\s{}()\[\]\-+*]+/, '').match(/^([A-Za-z_][\w']*)/)?.[1];
        if (name && !allowed.has(name)) issues.add(`Tactic not yet introduced: ${name}`);
      }
    }
  }
  return [...issues];
}
